import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  buildValidFqns,
  pruneSelectionToCatalog,
  reconcileSelectionWithCatalog,
  setsEqual,
} from "../selection";
import type { McpServer } from "../types";

type UseSelectionSyncProps = {
  servers: McpServer[];
  selectedFqns?: string[];
  defaultSelectedFqns?: string[];
  onChange?: (selectedFqns: string[]) => void;
};

export type SelectionSync = {
  /**
   * The current selection for the UI. May briefly include a default whose server
   * is still loading; such FQNs are never reported to `onChange` (see below), so
   * treat `onChange` — not this set — as the source of truth for persistence.
   */
  selected: Set<string>;
  /** Apply a user-initiated selection change (deduped; reports only in-catalog FQNs). */
  commitSelection: (next: Set<string>) => void;
};

/** Stable, order-independent identity for a selection, used to dedupe `onChange`. */
const selectionKey = (fqns: Set<string>): string => [...fqns].sort().join("\0");

/**
 * Owns the picker's selection: controlled/uncontrolled ownership, reconciliation
 * against a catalog that may still be loading, and `onChange` notification.
 *
 * The hard requirement this encapsulates: `servers` can arrive asynchronously and
 * in pieces, so a selection (especially uncontrolled defaults) must survive a
 * partially-loaded catalog. An uncontrolled picker gets no signal that loading
 * has finished, so it never prunes a not-yet-seen FQN — only drift (an FQN that
 * was valid and then vanished) is dropped. A controlled picker treats its props
 * as authoritative and prunes unknown FQNs immediately. Both notification paths —
 * a user edit (`commitSelection`) and catalog drift (the reconciliation effect) —
 * funnel through one `emit` helper, which dedupes by value and strips any FQN not
 * in the current catalog, so the parent is never notified twice nor handed a tool
 * that doesn't exist.
 */
export function useSelectionSync({
  servers,
  selectedFqns,
  defaultSelectedFqns,
  onChange,
}: UseSelectionSyncProps): SelectionSync {
  const isControlled = selectedFqns !== undefined;
  const [uncontrolledSelected, setUncontrolledSelected] = useState(
    () => new Set(defaultSelectedFqns)
  );
  const rawSelected = useMemo(
    () => new Set(isControlled ? selectedFqns : uncontrolledSelected),
    [isControlled, selectedFqns, uncontrolledSelected]
  );

  // Every FQN the catalog has ever advertised. Snapshotted *after* commit (see the
  // effect below) and read during render, so reconcile can tell "seen before, now
  // gone" (drift → drop) from "never seen" (keep while the catalog may still load).
  const everValidFqnsRef = useRef(new Set<string>());
  const hasReconciledRef = useRef(false);
  const lastEmittedSelectionRef = useRef<string | null>(null);

  const validFqns = useMemo(() => buildValidFqns(servers), [servers]);
  // Keep FQNs we can't yet validate when the catalog isn't authoritative: there's
  // nothing to validate against (empty), or the picker is uncontrolled, where an
  // async catalog gives no signal that loading has finished — pruning a not-yet-
  // loaded default would silently drop the user's pending selection. Drift (an FQN
  // that was valid and then vanished) is always pruned, regardless of this flag.
  const deferUnknownPruning = validFqns.size === 0 || !isControlled;

  const selected = useMemo(
    () =>
      reconcileSelectionWithCatalog(
        rawSelected,
        validFqns,
        everValidFqnsRef.current,
        deferUnknownPruning
      ),
    [rawSelected, validFqns, deferUnknownPruning]
  );

  // Accumulate the "ever valid" snapshot after commit. Read during render but
  // updated post-render, hence an effect rather than a derived value.
  useEffect(() => {
    for (const fqn of validFqns) {
      everValidFqnsRef.current.add(fqn);
    }
  }, [validFqns]);

  const emit = useCallback(
    (next: Set<string>) => {
      // Never report an FQN absent from the current catalog. A pending default
      // for a not-yet-loaded server (or any genuinely stale FQN) is kept in the
      // internal store but withheld from `onChange` until its tool actually
      // exists — so the consumer only ever persists real, in-catalog tools.
      const valid = new Set([...next].filter((fqn) => validFqns.has(fqn)));
      const key = selectionKey(valid);
      if (key === lastEmittedSelectionRef.current) {
        return;
      }
      lastEmittedSelectionRef.current = key;
      onChange?.(Array.from(valid));
    },
    [onChange, validFqns]
  );

  // Reconcile the selection with catalog drift (servers arriving or leaving).
  // User edits go through `commitSelection`; this handles only changes the user
  // didn't initiate, and stays silent on mount so a settling catalog never looks
  // like a user action.
  useEffect(() => {
    if (setsEqual(selected, rawSelected)) {
      if (validFqns.size > 0) {
        hasReconciledRef.current = true;
        // A user edit can emit a partial (or empty) selection while the catalog
        // is still loading — `emit` drops FQNs not yet in `validFqns`. Once the
        // catalog grows there's no drift to trigger the branch below, so re-emit
        // here to report the now-valid set. `emit` dedupes (a no-op when nothing
        // changed) and stays silent for an untouched initial selection, since
        // nothing has been emitted yet.
        if (lastEmittedSelectionRef.current !== null) {
          emit(selected);
        }
      }
      return;
    }
    if (validFqns.size === 0) {
      return;
    }
    if (!isControlled) {
      setUncontrolledSelected(selected);
    }
    // Stay silent until the selection has settled against a non-empty catalog
    // once, so a loading catalog never looks like a user action.
    if (isControlled || hasReconciledRef.current) {
      emit(selected);
    }
    hasReconciledRef.current = true;
  }, [selected, rawSelected, isControlled, validFqns.size, emit]);

  const commitSelection = useCallback(
    (next: Set<string>) => {
      // Mirror reconcile: prune only when the catalog is authoritative. While
      // deferring, a toggle must not strip a not-yet-loaded pending default.
      const pruned = deferUnknownPruning
        ? next
        : pruneSelectionToCatalog(next, validFqns);
      if (setsEqual(pruned, selected)) {
        return;
      }
      if (!isControlled) {
        setUncontrolledSelected(pruned);
      }
      emit(pruned);
    },
    [deferUnknownPruning, validFqns, selected, isControlled, emit]
  );

  return { selected, commitSelection };
}
